import styled, { css } from 'styled-components';
import { glassMedium } from './glassmorphism';
// eslint-disable-next-line no-unused-vars
import { shimmerAnimation } from './animations';

/**
 * Common button styles with consistent design language
 */

// Base button styles
const buttonBase = css`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.7s ease;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

// Primary button
export const Button = styled.button`
  ${buttonBase}
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
  }
`;

// Secondary button
export const SecondaryButton = styled.button`
  ${buttonBase}
  background: rgba(15, 23, 42, 0.5);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover:not(:disabled) {
    background: rgba(15, 23, 42, 0.7);
  }
`;

// Success button
export const SuccessButton = styled.button`
  ${buttonBase}
  background: rgba(16, 185, 129, 0.7);
  color: white;
  border: 1px solid rgba(16, 185, 129, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.9);
  }
`;

// Warning button
export const WarningButton = styled.button`
  ${buttonBase}
  background: rgba(245, 158, 11, 0.7);
  color: white;
  border: 1px solid rgba(245, 158, 11, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(245, 158, 11, 0.9);
  }
`;

// Danger button
export const DangerButton = styled.button`
  ${buttonBase}
  background: rgba(239, 68, 68, 0.7);
  color: white;
  border: 1px solid rgba(239, 68, 68, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.9);
  }
`;

// Outline button
export const OutlineButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

// Link button (looks like a link)
export const LinkButton = styled.button`
  background: none;
  border: none;
  color: rgba(99, 102, 241, 0.9);
  font-weight: 500;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    color: rgba(99, 102, 241, 1);
    text-decoration: underline;
    transform: none;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Icon button (circular)
export const IconButton = styled.button`
  width: ${props => props.size || '2.5rem'};
  height: ${props => props.size || '2.5rem'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${glassMedium}
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    background: rgba(15, 23, 42, 0.6);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Loading button state
export const loadingStyles = css`
  position: relative;
  color: transparent !important;
  pointer-events: none;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 20px;
    height: 20px;
    margin-left: -10px;
    margin-top: -10px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
